/**
 * syncStore — Gerencia fila de sincronização com Google Drive (debounce, retry, status)
 *
 * Consumidores: SyncStatusIndicator, useSync, offlineDetection
 * Dependências: googleDrive
 * Persistência: memória (fila transiente, dados persistidos via googleDrive)
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { SyncStatus, SyncOperation } from '../types/sync';
import { nanoid } from 'nanoid';
import { googleDrive } from '../services/googleDrive';
import { logger } from '../utils/logger';

const DEBOUNCE_DELAY = 2000; // 2 seconds
const MAX_RETRIES = 3;

interface SyncState {
  status: SyncStatus;
  queue: SyncOperation[];
  lastSyncTime: string | null;
  error: string | null;
  debounceTimer: NodeJS.Timeout | null;
  /** Número de operações pendentes na fila offline (IndexedDB) */
  queuedCount: number;
  /** Indica que uma sincronização cross-device está em andamento (visibilitychange) */
  isCrossDeviceSyncing: boolean;
  /** Mensagem exibida durante sincronização cross-device */
  crossDeviceSyncMessage: string | null;

  addOperation: (operation: Omit<SyncOperation, 'id' | 'timestamp' | 'retries'>) => void;
  processQueue: () => Promise<void>;
  retrySync: () => Promise<void>;
  clearQueue: () => void;
  setStatus: (status: SyncStatus) => void;
  scheduleSync: () => void;
  /** Atualiza queuedCount lendo do IndexedDB */
  updateQueuedCount: () => Promise<void>;
  /** Define status da sincronização cross-device (UX indicator) */
  setCrossDeviceSyncStatus: (syncing: boolean, message?: string | null) => void;
}

export const useSyncStore = create<SyncState>()(
  devtools(
    (set, get) => ({
      status: 'idle',
      queue: [],
      lastSyncTime: null,
      error: null,
      debounceTimer: null,
      queuedCount: 0,
      isCrossDeviceSyncing: false,
      crossDeviceSyncMessage: null,
      
      setCrossDeviceSyncStatus: (syncing, message) => {
        set({
          isCrossDeviceSyncing: syncing,
          crossDeviceSyncMessage: message ?? null,
        });
      },

      addOperation: (operation) => {
        const fullOperation: SyncOperation = {
          ...operation,
          id: nanoid(),
          timestamp: new Date().toISOString(),
          retries: 0,
        };
        
        set((state) => ({
          queue: [...state.queue, fullOperation],
        }));
        
        
        // Schedule sync with debounce
        get().scheduleSync();
      },
      
      scheduleSync: () => {
        const state = get();
        
        // Clear existing timer
        if (state.debounceTimer) {
          clearTimeout(state.debounceTimer);
        }
        
        // Schedule new sync after debounce delay
        const timer = setTimeout(() => {
          get().processQueue();
        }, DEBOUNCE_DELAY);
        
        set({ debounceTimer: timer });
      },
      
      processQueue: async () => {
        const state = get();
        
        // Check if queue is empty
        if (state.queue.length === 0) {
          set({ status: 'idle' });
          return;
        }
        
        // Check if offline
        if (!navigator.onLine) {
          set({ status: 'offline' });
          return;
        }
        
        set({ status: 'syncing', error: null });
        
        // Process operations sequentially
        const queue = [...state.queue];
        
        for (const operation of queue) {
          try {
            
            // Execute operation based on type
            switch (operation.type) {
              case 'create':
                await handleCreateOperation(operation);
                break;
              case 'update':
                await handleUpdateOperation(operation);
                break;
              case 'delete':
                await handleDeleteOperation(operation);
                break;
            }
            
            // Remove successful operation from queue
            set((state) => ({
              queue: state.queue.filter((op) => op.id !== operation.id),
            }));
            
            
          } catch (error: unknown) {
            logger.error('SyncStore', `Operation failed: ${operation.id}`, error);

            // Increment retry counter
            const updatedOperation = { ...operation, retries: operation.retries + 1 };

            if (updatedOperation.retries >= MAX_RETRIES) {
              // Max retries reached, mark as error
              logger.error('SyncStore', `Max retries reached for operation: ${operation.id}`);
              set({
                status: 'error',
                error: `Failed to sync after ${MAX_RETRIES} attempts: ${error instanceof Error ? error.message : String(error)}`,
              });
              
              // Remove failed operation from queue
              set((state) => ({
                queue: state.queue.filter((op) => op.id !== operation.id),
              }));
              
              return; // Stop processing queue
            } else {
              // Move operation to end of queue for retry
              set((state) => ({
                queue: [
                  ...state.queue.filter((op) => op.id !== operation.id),
                  updatedOperation,
                ],
              }));
              
            }
          }
        }
        
        // All operations processed successfully
        set({
          status: 'idle',
          lastSyncTime: new Date().toISOString(),
          error: null,
        });
        
      },
      
      retrySync: async () => {
        const state = get();

        if (state.queue.length > 0) {
          // Reset retry counters and reprocess
          set((s) => ({
            queue: s.queue.map((op) => ({ ...op, retries: 0 })),
            error: null,
          }));
          await get().processQueue();
        } else if (state.status === 'error' || state.status === 'offline') {
          // Queue empty but status stuck — clear error state
          set({ status: 'idle', error: null });
        }
      },

      clearQueue: () => {
        set({ queue: [], status: 'idle', error: null });
      },
      
      setStatus: (status) => {
        set({ status });
      },

      updateQueuedCount: async () => {
        try {
          const { getQueueCount } = await import('../services/offlineQueue');
          const count = await getQueueCount();
          set({ queuedCount: count });
          // Atualizar status para 'queued' se offline com itens pendentes
          if (count > 0 && !navigator.onLine && get().status !== 'syncing') {
            set({ status: 'queued' });
          } else if (count === 0 && get().status === 'queued') {
            set({ status: navigator.onLine ? 'idle' : 'offline' });
          }
        } catch {
          // IndexedDB indisponível — manter count anterior
        }
      },
    }),
    { name: 'SyncStore' }
  )
);

/**
 * Handle create operation
 */
async function handleCreateOperation(_operation: SyncOperation): Promise<void> {
  const appFolderId = googleDrive.getAppFolderId();
  if (!appFolderId) {
    throw new Error('Google Drive not initialized');
  }
  
  // TODO: Implement file creation in Google Drive
  // const fileId = await googleDrive.createFile(
  //   appFolderId,
  //   `${operation.resource}-${operation.data.id}.json`,
  //   JSON.stringify(operation.data, null, 2)
  // );
  
}

/**
 * Handle update operation
 */
async function handleUpdateOperation(operation: SyncOperation): Promise<void> {
  // For now, we'll need to store fileId mapping in the data
  // In a real implementation, we'd maintain a mapping of resource IDs to Google Drive file IDs
  const data = operation.data as Record<string, unknown>;
  const fileId = data.driveFileId;

  if (!fileId) {
    throw new Error('No Google Drive file ID found for update operation');
  }

  // TODO: Implement file update in Google Drive
  // await googleDrive.updateFile(fileId, JSON.stringify(operation.data, null, 2));
}

/**
 * Handle delete operation
 */
async function handleDeleteOperation(operation: SyncOperation): Promise<void> {
  const data = operation.data as Record<string, unknown>;
  const fileId = data.driveFileId;
  
  if (!fileId) {
    throw new Error('No Google Drive file ID found for delete operation');
  }
  
  // TODO: Implement file deletion in Google Drive
  // await googleDrive.deleteFile(fileId);
}

